"use client";

import { forwardRef, type VideoHTMLAttributes } from "react";
import { useDerivedVideoPoster } from "@/lib/media/use-derived-video-poster";
import { HlsVideo } from "./hls-video";

type VideoMediaProps = Omit<VideoHTMLAttributes<HTMLVideoElement>, "src"> & {
  src: string;
  /** HLS master playlist URL. Preferred whenever playable. */
  hlsSrc?: string;
  mimeType?: string;
  posterUrl?: string;
  derivePoster?: boolean;
};

export const VideoMedia = forwardRef<HTMLVideoElement, VideoMediaProps>(
  function VideoMedia(
    {
      src,
      hlsSrc,
      mimeType,
      posterUrl,
      derivePoster = false,
      preload = "metadata",
      ...restProps
    },
    ref,
  ) {
    const resolvedPosterUrl = useDerivedVideoPoster({
      // New uploads already include a poster. Deriving one from a remote video
      // downloads and decodes that video in the browser, so it must be an
      // explicit opt-in rather than work every mounted card performs.
      videoUrl: derivePoster ? src : undefined,
      explicitPosterUrl: posterUrl,
    });

    return (
      <HlsVideo
        src={src}
        hlsSrc={hlsSrc}
        mimeType={mimeType}
        ref={ref}
        preload={preload}
        poster={resolvedPosterUrl}
        {...restProps}
      />
    );
  },
);
